import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import Sidebar from './Sidebar';
import { useSidebar } from '../hooks';
import { Session } from "./utils/session";

function ErrorLogs() {
  const { toggleSidebar } = useSidebar();
  const navigate = useNavigate();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [stackTrace, setStackTrace] = useState('');

  // 🔹 Get token from Session
  const authHeaders = () => {
    const token = Session.getToken();
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    };
  };

  // 🔹 Load error logs
  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const resp = await fetch(`${API_BASE_URL}/api/ErrorLog/list`, {
        headers: authHeaders()
      });

      if (!resp.ok) {
        throw new Error(`Failed to load logs. Status: ${resp.status}`);
      }

      const data = await resp.json();
      const logsArray = Array.isArray(data) ? data : data.data || [];
      setLogs(logsArray);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to load error logs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  // 🔹 View stack trace
  const openStackTrace = async (id) => {
    try {
      const resp = await fetch(`${API_BASE_URL}/api/ErrorLog/${id}`, {
        headers: authHeaders()
      });

      if (!resp.ok) throw new Error(`Failed to fetch stack trace. Status: ${resp.status}`);
      const data = await resp.json();
      setStackTrace(data.stackTrace || 'No stack trace available');
      setShowModal(true);
    } catch (err) {
      console.error(err);
      alert(err.message || 'Unable to load stack trace');
    }
  };

  return (
    <div>
      <div id="sidebar-overlay" />
      <Sidebar />

      <div id="content">
        <div className="d-flex align-items-center mb-3">
          <button
            type="button"
            id="sidebarCollapse"
            className="btn btn-toggle me-3"
            onClick={toggleSidebar}
          >
            <i className="fas fa-bars" />
          </button>
          <h5 className="mb-0">Error Logs</h5>
        </div>

        <div className="container-fluid">
          <div className="card shadow-sm">
            <div className="card-body p-0">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Source</th>
                    <th>User</th>
                    <th>Error Message</th>
                    <th>Added On</th>
                    <th style={{ width: 80 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan="5" className="text-center py-4">
                        Loading...
                      </td>
                    </tr>
                  )}

                  {!loading && error && (
                    <tr>
                      <td colSpan="5" className="text-center py-4 text-danger">
                        {error}
                      </td>
                    </tr>
                  )}

                  {!loading && !error && logs.length === 0 && (
                    <tr>
                      <td colSpan="5" className="text-center py-4 text-muted">
                        No error logs found
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    !error &&
                    logs.map((log) => (
                      <tr key={log.errorLogId}>
                        <td>{log.source}</td>
                        <td>{log.userName || log.username}</td>
                        <td className="text-truncate" style={{ maxWidth: 350 }}>
                          {log.errorMessage}
                        </td>
                        <td>
                          {log.addedOn
                            ? new Date(log.addedOn).toLocaleString()
                            : ''}
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => openStackTrace(log.errorLogId)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* 🔹 StackTrace Modal */}
      {showModal && (
        <div className="modal d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div 
                  className="modal-content"
                  style={{ zIndex: 100000 }}
                >
              <div className="modal-header">
                <h5 className="modal-title">Stack Trace</h5>
                {/* ✅ Fixed close button */}
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <pre
                  style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxHeight: '400px',
                    overflowY: 'auto',
                    background: '#f8f9fa',
                    padding: '12px',
                    borderRadius: '6px'
                  }}
                >
                  {stackTrace}
                </pre>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" />
        </div>
      )}
    </div>
  );
}

export default ErrorLogs;
